import { error } from '@react/react-spectrum/Toast';
import qs from 'query-string';
import { useContext, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import { browseApi, commonApi, handleApiCall } from '@exchange-frontends/api';
import { useIMS, useSelectedFilters } from '@exchange-frontends/custom-hooks';

import { ContainerContext } from '../../../utils';
import { ConfigContext } from '../components/App';
import { useStore } from '../store';

export const useLocalImageURL = (localImage) => {
  const { host = '' } = useContext(ConfigContext) ?? {};
  return `${host}/${localImage}`;
};

export const useSearchResults = (search, filterKeys, page, cloud) => {
  const PAGE_SIZE = 12;
  const { ims } = useContext(ContainerContext);
  const accessToken = useMemo(() => ims?.getAccessToken()?.token, [ims]);
  const [apps, setApps] = useState(Array(PAGE_SIZE).fill(null));
  const [totalApps, setTotalApps] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [encounteredError, setEncounteredError] = useState(false);
  const [parsedLocationSearch, setParsedLocationSearch] = useState(null);
  const selectedFilters = useSelectedFilters(cloud);
  const filters = useStore((store) => store.filters);
  const selectedTagIds = useMemo(
    () =>
      cloud === 'cc'
        ? filters?.tag?.filter((tag) => qs.parse(search).tag?.includes(tag?.name))?.map((tag) => tag?.key)
        : null,
    [filters, cloud, search]
  );

  useEffect(() => {
    let mounted = true;
    let handler;
    const parsedSearch = qs.parse(search);
    setParsedLocationSearch(parsedSearch);
    const { q, product, sort, price, page: _urlPage, partnerLevel, aepServices, listingType, solutionType, geographies, ...rest } = parsedSearch;

    if (cloud === 'ec') {
      const searchCloud = product === 'SIGN' ? 'DC' : cloud.toUpperCase();
      const getAppType = () => {
        if (listingType === 'solutions') {
          return 'SLN';
        }
        if (listingType === 'applications') {
          return parsedSearch.appType;
        }
      };

      
      handler = handleApiCall(browseApi.search, {
        fullDetails: false,
        featured: false,
        hostApp: product === 'All' ? undefined : product,
        partnerLevel: partnerLevel === 'All' ? undefined : partnerLevel,
        solutionType: solutionType === 'All' ? undefined : solutionType,
        geographies: geographies === 'All' ? undefined : geographies,
        aepServices: aepServices === 'Experience Platform Launch' ? 'APL' : aepServices,
        sortBy: sort,
        pageSize: PAGE_SIZE,
        pageIndex: page - 1,
        appType: getAppType(),
        filterKeys,
        q,
        cloud: searchCloud,
        authToken: accessToken,
        ...rest,
      });
    } else if (cloud === 'cc') {
      handler = handleApiCall(commonApi.ccesV3Extensions, {
        pageSize: PAGE_SIZE,
        pageIndex: page - 1,
        resultType: 'card',
        searchString: q,
        sort,
        price,
        product,
        categoryIds: selectedTagIds?.length ? selectedTagIds : undefined,
        authToken: accessToken,
      });
    }
    (async () => {
      if (product && cloud) {
        try {
          setEncounteredError(false);
          useStore.setState({ loading: true });
          const response = await handler.run();
          if (mounted) {
            const { cloudApps, categories = {}, pages, totalCount } = response;
            setApps(cloudApps);
            setTotalPages(pages);
            setTotalApps(totalCount);
            if (cloud === 'ec') {
              if (categories?.aepServices && categories?.aepServices[0]?.name === 'APL') {
                categories.aepServices[0].name = 'Experience Platform Launch';
              }
              useStore.setState({ filters: { ...selectedFilters, ...categories } });
            }
          }
        } catch (err) {
          // CCES asked us to redirect to login on 429
          if (err.code === 429) {
            if (!ims?.isMock) {
              ims.signIn();
            } else {
              error('You need to login in order to continue', { timeout: 0 });
              setEncounteredError(true);
            }
            // 20 is the error code for aborted requests
          } else if (err.code !== 20) {
            error(err.userMessage, { timeout: 0 });
            console.error(err);
            setEncounteredError(true);
          }
        } finally {
          if (mounted) {
            useStore.setState({ loading: false });
          }
        }
      }
    })();

    return () => {
      mounted = false;
      handler?.controller.abort();
    };
  }, [search, filterKeys, page, selectedFilters, cloud, selectedTagIds, ims, accessToken]);

  return { apps, totalApps, totalPages, encounteredError, parsedLocationSearch };
};

export const useAppIntegrations = () => {
  const { data, error } = useSWR('get_app_integrations', browseApi.appIntegrations);

  return {
    apps: data ?? new Array(9).fill(null),
    isLoading: !error && !data,
    isError: error,
  };
};
